import { useState, useEffect, useRef, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Search, Camera, ScanLine, Trash2, Plus, Minus, ShoppingCart, Loader2,
  DollarSign, Smartphone, CheckCircle2, X, Boxes,
} from "lucide-react";
import api, { API, formatApiError } from "@/lib/api";
import { gradeLabel } from "@/lib/grade";
import QrScanner from "@/components/QrScanner";
import CheckoutModal from "@/components/CheckoutModal";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const fmt = (n) => `$${Number(n || 0).toFixed(2)}`;
const LOW_PCT = 90; // below this, confirm before applying

export default function Selling() {
  const qc = useQueryClient();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [cart, setCart] = useState([]); // {card, quantity, pct}
  const [cartPct, setCartPct] = useState(100);
  const [cartPctEdit, setCartPctEdit] = useState(null); // raw text while focused
  const [lineEdit, setLineEdit] = useState(null); // { id, text } while a line % is focused
  const [pendingLow, setPendingLow] = useState(null); // { what, onYes } confirmation
  const [flatTotal, setFlatTotal] = useState("");
  const [scanOpen, setScanOpen] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [paymentQrs, setPaymentQrs] = useState({});
  const [loadingBoxed, setLoadingBoxed] = useState(false);
  const scanInputRef = useRef(null);

  useEffect(() => {
    api.get("/settings").then(({ data }) => {
      setPaymentQrs(data.payment_qrs || {});
      if (data.sell_default_pct) setCartPct(data.sell_default_pct);
    }).catch(() => {});
  }, []);

  // debounced inventory search
  useEffect(() => {
    if (query.trim().length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await api.get("/sell/search", { params: { q: query } });
        setResults(data);
      } catch { /* ignore */ } finally { setSearching(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [query]);

  const addToCart = useCallback((card) => {
    setCart((prev) => {
      const idx = prev.findIndex((l) => l.card.id === card.id);
      if (idx >= 0) {
        const next = [...prev];
        const line = next[idx];
        if (line.quantity < (card.quantity || 0)) line.quantity += 1;
        else toast.warning(`Only ${card.quantity} in stock`);
        return next;
      }
      return [...prev, { card, quantity: 1, pct: null }];
    });
  }, []);

  const addByToken = useCallback(async (token) => {
    const t = (token || "").trim();
    if (!t) return;
    try {
      const { data } = await api.get(`/cards/by-qr/${encodeURIComponent(t)}`);
      if ((data.quantity || 0) < 1) { toast.error(`${data.name} is out of stock`); return; }
      addToCart(data);
      toast.success(`Added ${data.name}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }, [addToCart]);

  const onScanInputKey = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addByToken(scanInputRef.current.value);
      scanInputRef.current.value = "";
    }
  };

  const addAllBoxed = async () => {
    setLoadingBoxed(true);
    try {
      const { data } = await api.get("/sell/boxed-eligible");
      if (!data.length) { toast.info("No in-stock cards are on the Boxed buylist right now"); return; }
      setCart((prev) => {
        const next = [...prev];
        for (const card of data) {
          const idx = next.findIndex((l) => l.card.id === card.id);
          if (idx >= 0) next[idx] = { ...next[idx], card, quantity: card.quantity || 1 };
          else next.push({ card, quantity: card.quantity || 1, pct: null });
        }
        return next;
      });
      toast.success(`Added ${data.length} Boxed buylist card${data.length > 1 ? "s" : ""} to the cart`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoadingBoxed(false); }
  };

  const setQty = (id, q) => setCart((prev) => prev.map((l) => l.card.id === id
    ? { ...l, quantity: Math.max(0, Math.min(l.card.quantity || 1, q)) } : l));
  // The "down" button: steps the quantity down to 0, then removes the line on the next press.
  const stepDown = (l) => (l.quantity <= 0 ? removeLine(l.card.id) : setQty(l.card.id, l.quantity - 1));
  const setLineValue = (id, v) => setCart((prev) => prev.map((l) => l.card.id === id ? { ...l, pct: v } : l));
  const removeLine = (id) => setCart((prev) => prev.filter((l) => l.card.id !== id));

  // Apply a cart-wide % — confirm first if it's below 90%.
  const applyCartPct = (val) => {
    let n = Math.round(Number(val));
    if (isNaN(n)) return;
    n = Math.max(0, Math.min(100, n));
    if (n < LOW_PCT) setPendingLow({ what: `sell the entire cart at ${n}% of market`, onYes: () => setCartPct(n) });
    else setCartPct(n);
  };

  // Apply a per-line % override — confirm first if below 90%. Empty clears the override.
  const applyLinePct = (id, raw) => {
    if (String(raw).trim() === "") { setLineValue(id, null); return; }
    let n = Math.round(Number(raw));
    if (isNaN(n)) return;
    n = Math.max(0, Math.min(100, n));
    if (n < LOW_PCT) {
      const name = cart.find((l) => l.card.id === id)?.card.name || "this card";
      setPendingLow({ what: `sell ${name} at ${n}% of market`, onYes: () => setLineValue(id, n) });
    } else setLineValue(id, n);
  };

  const effPct = (l) => (l.pct != null ? l.pct : (Number(cartPct) || 100));
  const lineTotal = (l) => Number(l.card.tcg_price || 0) * effPct(l) / 100 * l.quantity;
  const total = cart.reduce((s, l) => s + lineTotal(l), 0);
  const fullTotal = cart.reduce((s, l) => s + Number(l.card.tcg_price || 0) * l.quantity, 0);
  const boxedPayout = cart.reduce((s, l) => s + (l.card.boxed_buy != null ? Number(l.card.boxed_buy) * l.quantity : 0), 0);
  const boxedLines = cart.filter((l) => l.card.boxed_buy != null).length;
  const flat = flatTotal !== "" && Number(flatTotal) > 0 ? Number(flatTotal) : null;
  const grand = flat != null ? flat : total;

  const bumpCartPct = (d) => applyCartPct((Number(cartPct) || 100) + d);

  const doCheckout = async (method, app = "") => {
    try {
      const items = cart.filter((l) => l.quantity > 0).map((l) => ({ card_id: l.card.id, quantity: l.quantity, pct: effPct(l) }));
      const body = { items, payment_method: method, payment_app: app };
      if (flat != null) body.override_total = flat;
      const { data } = await api.post("/sell/checkout", body);
      toast.success(`Sale complete · ${fmt(data.total)} (${data.order_ref})`);
      setCart([]); setFlatTotal(""); setCheckoutOpen(false);
      qc.invalidateQueries({ queryKey: ["cards"] });
      qc.invalidateQueries({ queryKey: ["stats"] });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <div className="mb-6">
        <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-100">Selling</h1>
        <p className="text-sm text-slate-400 mt-1">Ring up in-person sales. Scan a sticker, search, or type a code to build the cart.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Add column */}
        <div className="lg:col-span-2 space-y-4">
          <div className="rounded-2xl border p-4" style={{ background: "var(--pk-surface)", borderColor: "var(--pk-border)" }}>
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg border focus-within:ring-2 focus-within:ring-amber-500/50" style={{ background: "var(--pk-card)", borderColor: "var(--pk-border)" }}>
              <Search className="h-4 w-4 text-slate-500" />
              <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search inventory by name, SKU, set..."
                data-testid="sell-search-input" className="w-full bg-transparent outline-none text-sm text-slate-100 placeholder:text-slate-600" />
              {searching && <Loader2 className="h-4 w-4 animate-spin text-slate-500" />}
            </div>

            <div className="flex gap-2 mt-3">
              <button onClick={() => setScanOpen(true)} data-testid="sell-camera-button"
                className="flex-1 flex items-center justify-center gap-2 py-2 rounded-lg border text-sm text-slate-200 hover:bg-[var(--pk-hover)]" style={{ borderColor: "var(--pk-border)" }}>
                <Camera className="h-4 w-4" /> Camera scan
              </button>
              <div className="flex-1 flex items-center gap-2 px-3 py-2 rounded-lg border" style={{ background: "var(--pk-card)", borderColor: "var(--pk-border)" }}>
                <ScanLine className="h-4 w-4 text-slate-500" />
                <input ref={scanInputRef} onKeyDown={onScanInputKey} placeholder="Scanner / type code + Enter"
                  data-testid="sell-scan-input" className="w-full bg-transparent outline-none text-xs text-slate-100 placeholder:text-slate-600 font-mono-num" />
              </div>
            </div>

            <button onClick={addAllBoxed} disabled={loadingBoxed} data-testid="sell-add-all-boxed-button"
              className="mt-3 w-full flex items-center justify-center gap-2 py-2.5 rounded-lg border border-blue-500/50 text-blue-300 text-sm font-semibold hover:bg-blue-500/10 disabled:opacity-50 transition-colors">
              {loadingBoxed ? <Loader2 className="h-4 w-4 animate-spin" /> : <Boxes className="h-4 w-4" />} Add all Boxed.gg cards
            </button>
            <p className="text-[11px] text-slate-500 mt-1.5">Adds every in-stock card Boxed is currently buying (at full quantity) so you can remove a whole buylist order in one checkout.</p>

            {results.length > 0 && (
              <div className="mt-3 max-h-80 overflow-y-auto rounded-lg border divide-y" style={{ borderColor: "var(--pk-border)" }} data-testid="sell-search-results">
                {results.map((c) => (
                  <button key={c.id} onClick={() => addToCart(c)} data-testid="sell-result-item"
                    className="w-full flex items-center gap-3 px-3 py-2 hover:bg-[var(--pk-hover)] text-left" style={{ borderColor: "var(--pk-border-subtle)" }}>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-slate-100 truncate">{c.name}</div>
                      <div className="text-[11px] text-slate-500 truncate">{c.set_name} · {c.item_type === "slab" ? gradeLabel(c) : c.condition} · {c.quantity} in stock</div>
                    </div>
                    <span className="font-mono-num text-sm text-amber-400">{fmt(c.tcg_price)}</span>
                    <Plus className="h-4 w-4 text-slate-500" />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Cart column */}
        <div className="lg:col-span-3">
          <div className="rounded-2xl border" style={{ background: "var(--pk-surface)", borderColor: "var(--pk-border)" }}>
            <div className="px-5 py-4 border-b flex items-center justify-between" style={{ borderColor: "var(--pk-border)" }}>
              <div className="flex items-center gap-2 text-slate-100 font-heading font-bold"><ShoppingCart className="h-5 w-5 text-amber-400" /> Cart ({cart.length})</div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-400">Buyer pays</span>
                <div className="flex items-center gap-1.5">
                  <button onClick={() => bumpCartPct(-5)} data-testid="sell-cart-pct-minus" title="-5%" className="h-8 w-8 rounded-lg border flex items-center justify-center text-slate-400 hover:text-amber-400" style={{ borderColor: "var(--pk-border)" }}><Minus className="h-3.5 w-3.5" /></button>
                  <div className="flex items-center gap-1 px-2 py-1.5 rounded-lg border w-20 focus-within:ring-2 focus-within:ring-amber-500/50" style={{ background: "var(--pk-card)", borderColor: "var(--pk-border)" }}>
                    <input type="number" inputMode="numeric" value={cartPctEdit ?? String(cartPct)}
                      onFocus={(e) => { setCartPctEdit(String(cartPct)); e.target.select(); }}
                      onChange={(e) => setCartPctEdit(e.target.value)}
                      onBlur={(e) => { const raw = e.target.value; setCartPctEdit(null); if (raw.trim() !== "") applyCartPct(raw); }}
                      data-testid="sell-cart-pct-input" className="w-full bg-transparent outline-none text-sm text-slate-100 font-mono-num text-center" />
                    <span className="text-slate-500 text-sm">%</span>
                  </div>
                  <button onClick={() => bumpCartPct(5)} data-testid="sell-cart-pct-plus" title="+5%" className="h-8 w-8 rounded-lg border flex items-center justify-center text-slate-400 hover:text-amber-400" style={{ borderColor: "var(--pk-border)" }}><Plus className="h-3.5 w-3.5" /></button>
                </div>
              </div>
            </div>

            {cart.length === 0 ? (
              <div className="py-16 text-center text-sm text-slate-500">Cart is empty. Add cards to start a sale.</div>
            ) : (
              <div className="divide-y" style={{ borderColor: "var(--pk-border-subtle)" }}>
                {cart.map((l) => (
                  <div key={l.card.id} data-testid="sell-cart-line" className="px-4 sm:px-5 py-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-slate-100 truncate">{l.card.name}</div>
                      <div className="text-[11px] text-slate-500 truncate">{l.card.set_name} · {l.card.item_type === "slab" ? gradeLabel(l.card) : l.card.condition} · list {fmt(l.card.tcg_price)}</div>
                    </div>
                    <div className="flex items-center justify-between sm:justify-end gap-3">
                      <div className="flex items-center gap-1">
                        <button onClick={() => stepDown(l)} data-testid="sell-line-qty-minus" title={l.quantity <= 0 ? "Remove from cart" : (l.quantity === 1 ? "Set to 0" : "Decrease quantity")} className="h-8 w-8 rounded border flex items-center justify-center text-slate-400 hover:text-rose-400" style={{ borderColor: "var(--pk-border)" }}><Minus className="h-3.5 w-3.5" /></button>
                        <span className={`w-7 text-center font-mono-num text-sm ${l.quantity <= 0 ? "text-rose-400" : "text-slate-100"}`} data-testid="sell-line-qty">{l.quantity}</span>
                        <button onClick={() => setQty(l.card.id, l.quantity + 1)} data-testid="sell-line-qty-plus" title="Increase quantity" className="h-8 w-8 rounded border flex items-center justify-center text-slate-400 hover:text-emerald-400" style={{ borderColor: "var(--pk-border)" }}><Plus className="h-3.5 w-3.5" /></button>
                      </div>
                      <div className="flex items-center gap-1 px-2 py-1.5 rounded-lg border w-24 focus-within:ring-2 focus-within:ring-amber-500/50" style={{ background: "var(--pk-card)", borderColor: "var(--pk-border)" }} title="Override % for this card">
                        <input type="number" inputMode="numeric" value={lineEdit?.id === l.card.id ? lineEdit.text : (l.pct ?? "")} placeholder={String(Math.round(Number(cartPct)))}
                          onFocus={(e) => { setLineEdit({ id: l.card.id, text: l.pct != null ? String(l.pct) : "" }); e.target.select(); }}
                          onChange={(e) => setLineEdit({ id: l.card.id, text: e.target.value })}
                          onBlur={(e) => { const raw = e.target.value; setLineEdit(null); applyLinePct(l.card.id, raw); }}
                          data-testid="sell-line-pct-input" className="w-full bg-transparent outline-none text-sm text-slate-100 font-mono-num text-center" />
                        <span className="text-slate-500 text-xs">%</span>
                      </div>
                      <span className="w-20 text-right font-mono-num text-sm text-emerald-400">{fmt(lineTotal(l))}</span>
                      <button onClick={() => removeLine(l.card.id)} data-testid="sell-remove-line" className="text-slate-500 hover:text-rose-400"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="px-5 py-4 border-t" style={{ borderColor: "var(--pk-border)" }}>
              {fullTotal > total && (
                <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
                  <span>List total</span><span className="font-mono-num line-through">{fmt(fullTotal)}</span>
                </div>
              )}
              {flat != null && (
                <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
                  <span>% price</span><span className="font-mono-num line-through">{fmt(total)}</span>
                </div>
              )}
              <label className="flex items-center justify-between gap-3 mb-3">
                <span className="text-sm text-slate-300">Agreed total (flat)</span>
                <div className="flex items-center gap-1 px-2 py-1.5 rounded-lg border w-32 focus-within:ring-2 focus-within:ring-amber-500/50" style={{ background: "var(--pk-card)", borderColor: "var(--pk-border)" }} title="Type a negotiated flat total to override the %">
                  <span className="text-slate-500 text-sm">$</span>
                  <input type="number" inputMode="decimal" value={flatTotal} placeholder={total.toFixed(2)} onFocus={(e) => e.target.select()}
                    onChange={(e) => setFlatTotal(e.target.value)} data-testid="sell-flat-total-input"
                    className="w-full bg-transparent outline-none text-sm text-slate-100 font-mono-num text-right" />
                  {flat != null && <button onClick={() => setFlatTotal("")} data-testid="sell-flat-total-clear" className="text-slate-500 hover:text-rose-400"><X className="h-3.5 w-3.5" /></button>}
                </div>
              </label>
              {flat != null && fullTotal > 0 && (
                <div className="flex items-center justify-end -mt-2 mb-3 text-[11px] text-amber-400/90" data-testid="sell-flat-implied-pct">
                  flat {fmt(flat)} = {Math.round((flat / fullTotal) * 100)}% of list
                </div>
              )}
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm text-slate-300 font-semibold flex items-center gap-2">Total{flat != null && <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-400 border border-amber-500/30">flat</span>}</span>
                <span className="font-mono-num text-2xl font-bold text-slate-100" data-testid="sell-cart-total">{fmt(grand)}</span>
              </div>
              {boxedLines > 0 && (
                <div className="flex items-center justify-between mb-4 -mt-2 px-3 py-2 rounded-lg border border-blue-800/50 bg-blue-950/40" data-testid="sell-boxed-payout">
                  <span className="text-xs text-blue-300 flex items-center gap-1.5">
                    <Boxes className="h-3.5 w-3.5" /> Boxed pays (est.)
                    <span className="text-[10px] text-slate-500">· {boxedLines} of {cart.length} line{cart.length > 1 ? "s" : ""}</span>
                  </span>
                  <span className="font-mono-num text-base font-bold text-blue-400" data-testid="sell-boxed-payout-value">{fmt(boxedPayout)}</span>
                </div>
              )}
              <button onClick={() => setCheckoutOpen(true)} disabled={cart.length === 0} data-testid="sell-checkout-button"
                className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-amber-500 text-[#1a1205] font-semibold text-sm hover:bg-amber-400 disabled:opacity-50">
                <DollarSign className="h-4 w-4" /> Checkout
              </button>
            </div>
          </div>
        </div>
      </div>

      <QrScanner open={scanOpen} onClose={() => setScanOpen(false)} onScan={(t) => addByToken(t)} />
      {checkoutOpen && (
        <CheckoutModal total={grand} paymentQrs={paymentQrs} onClose={() => setCheckoutOpen(false)} onConfirm={doCheckout} />
      )}

      <AlertDialog open={!!pendingLow} onOpenChange={(o) => { if (!o) setPendingLow(null); }}>
        <AlertDialogContent data-testid="sell-low-pct-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm below 90%</AlertDialogTitle>
            <AlertDialogDescription>
              You're about to {pendingLow?.what}. That's below 90% of market — are you sure you want to do this?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="sell-low-pct-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="sell-low-pct-confirm" onClick={() => { pendingLow?.onYes?.(); setPendingLow(null); }}>
              Yes, apply it
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
